/* ============================================================
   ALL BLOWN UP: Standee Studio interactions (v3)
   ============================================================ */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(pointer: fine)').matches;

  /* ---------- Scroll reveal (observer + scroll fallback) ---------- */
  let pending = [...document.querySelectorAll('.reveal')];
  const show = el => { el.classList.add('in'); pending = pending.filter(p => p !== el); };
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
  }, { threshold: 0.14, rootMargin: '0px 0px -7% 0px' });
  pending.forEach(el => io.observe(el));
  const sweepReveals = () => {
    if (!pending.length) return;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    pending.slice().forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.93 && r.bottom > 0) { io.unobserve(el); show(el); }
    });
  };

  /* ---------- Header + progress + parallax ---------- */
  const header = document.getElementById('header');
  const progress = document.getElementById('progress');
  const parallax = [...document.querySelectorAll('.parallax')];
  let ticking = false;
  const update = () => {
    const y = window.scrollY;
    header.classList.toggle('scrolled', y > 20);
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    if (progress) progress.style.width = (docH > 0 ? (y / docH) * 100 : 0) + '%';
    if (!reduce) {
      const vh = window.innerHeight;
      parallax.forEach(p => {
        const r = p.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const off = (r.top + r.height / 2 - vh / 2) * -(parseFloat(p.dataset.par) || 0.1);
        p.style.transform = `translate3d(0, ${off.toFixed(1)}px, 0)`;
      });
    }
    sweepReveals();
    ticking = false;
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();

  /* ============================================================
     STANDEE WOBBLE PHYSICS
     ============================================================ */
  if (!reduce) {
    const standees = [...document.querySelectorAll('.standee')].map(el => ({
      el, angle: 0, vel: 0, target: 0, drag: false, range: parseFloat(el.dataset.range) || 160
    }));
    const K = 0.12, DAMP = 0.86; // spring + damping
    let running = false, rafId = null;
    const wake = () => { if (!running) { running = true; rafId = requestAnimationFrame(loop); } };

    // pointer proximity -> gentle lean
    if (fine) {
      window.addEventListener('mousemove', (e) => {
        let near = false;
        standees.forEach(s => {
          if (s.drag) return;
          const r = s.el.getBoundingClientRect();
          const cx = r.left + r.width / 2, cy = r.top + r.height * 0.85;
          const dx = e.clientX - cx, dy = e.clientY - cy;
          const dist = Math.hypot(dx, dy);
          if (dist < s.range) {
            const lean = (dx / s.range) * 7 * (1 - dist / s.range);
            s.target = Math.max(-7, Math.min(7, lean));
            near = true;
          } else s.target = 0;
        });
        if (near) wake();
      }, { passive: true });
    }

    // grab & rock
    standees.forEach(s => {
      const start = (e) => {
        s.drag = true; s.el.classList.add('dragging');
        s.startX = (e.touches ? e.touches[0].clientX : e.clientX);
        s.startAngle = s.angle;
      };
      const move = (e) => {
        if (!s.drag) return;
        const x = (e.touches ? e.touches[0].clientX : e.clientX);
        const r = s.el.getBoundingClientRect();
        s.angle = Math.max(-22, Math.min(22, s.startAngle + (x - s.startX) / (r.height * 0.5) * 18));
      };
      const end = () => {
        if (!s.drag) return;
        s.drag = false; s.el.classList.remove('dragging');
        s.vel = s.angle * -0.22; // release into a rock
        wake();
      };
      s.el.addEventListener('mousedown', (e) => { start(e); wake(); });
      s.el.addEventListener('touchstart', (e) => { start(e); wake(); }, { passive: true });
      window.addEventListener('mousemove', move, { passive: true });
      window.addEventListener('touchmove', move, { passive: true });
      window.addEventListener('mouseup', end);
      window.addEventListener('touchend', end);
      // quick tap = knock
      s.el.addEventListener('click', () => { if (Math.abs(s.angle) < 1) { s.vel += 6; wake(); } });
    });

    function loop() {
      let active = false;
      standees.forEach(s => {
        if (!s.drag) {
          s.vel += (s.target - s.angle) * K;
          s.vel *= DAMP;
          s.angle += s.vel;
          if (Math.abs(s.angle) < 0.01 && Math.abs(s.vel) < 0.01 && s.target === 0) s.angle = 0;
        }
        s.el.style.transform = `rotate(${s.angle.toFixed(2)}deg)`;
        if (s.drag || Math.abs(s.vel) > 0.01 || Math.abs(s.angle) > 0.01 || s.target !== 0) active = true;
      });
      if (active) { rafId = requestAnimationFrame(loop); } else { running = false; }
    }
  }

  /* ============================================================
     BLOW-UP SLIDER
     ============================================================ */
  const slider = document.getElementById('blowSlider');
  const blowEl = document.getElementById('blowStandee');
  const sizeEl = document.getElementById('blowSize');
  const stamp = document.getElementById('blowStamp');
  if (slider && blowEl) {
    const apply = () => {
      const v = +slider.value / 100;            // 0..1
      const h = 16 + v * 68;                     // 16%..84% height
      blowEl.style.height = h + '%';
      const ft = (0.8 + v * 5.2).toFixed(1);     // ~0.8ft .. 6.0ft
      if (sizeEl) sizeEl.textContent = ft;
      stamp.classList.toggle('show', v > 0.92);
    };
    slider.addEventListener('input', apply);
    apply();
  }

  /* ============================================================
     CART + CONFETTI
     ============================================================ */
  const cartCountEl = document.getElementById('cartCount');
  const toast = document.getElementById('toast');
  const toastName = document.getElementById('toastName');
  let cart = 0;
  document.querySelectorAll('.add').forEach(btn => {
    btn.addEventListener('click', (e) => {
      cart++;
      cartCountEl.textContent = cart;
      cartCountEl.classList.add('show');
      toastName.textContent = 'Added ' + (btn.dataset.name || 'item');
      toast.classList.add('show');
      clearTimeout(toast._t);
      toast._t = setTimeout(() => toast.classList.remove('show'), 2200);
      burst(e.clientX, e.clientY);
      document.getElementById('cartBtn')?.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.22)' }, { transform: 'scale(1)' }], { duration: 300 });
    });
  });
  function burst(x, y) {
    if (reduce) return;
    const colors = ['#e90c22', '#ec384a', '#ffd24a', '#17110f', '#ffffff'];
    for (let i = 0; i < 20; i++) {
      const p = document.createElement('i');
      const size = 6 + Math.random() * 6;
      p.style.cssText = `position:fixed;left:${x}px;top:${y}px;width:${size}px;height:${size}px;background:${colors[i % colors.length]};border-radius:${Math.random() > .5 ? '50%' : '2px'};z-index:10000;pointer-events:none`;
      document.body.appendChild(p);
      const ang = (Math.PI * 2 * i) / 20 + Math.random();
      const vel = 55 + Math.random() * 115;
      const dx = Math.cos(ang) * vel, dy = Math.sin(ang) * vel - 40;
      p.animate([{ transform: 'translate(0,0)', opacity: 1 }, { transform: `translate(${dx}px,${dy + 220}px) rotate(${Math.random() * 720}deg)`, opacity: 0 }],
        { duration: 900 + Math.random() * 500, easing: 'cubic-bezier(.2,.7,.3,1)' }).onfinish = () => p.remove();
    }
  }

  /* ---------- Mobile menu ---------- */
  const burger = document.getElementById('burger');
  const menu = document.getElementById('mobileMenu');
  burger?.addEventListener('click', () => { menu.classList.toggle('open'); burger.classList.toggle('active'); });
  menu?.querySelectorAll('a').forEach(a => a.addEventListener('click', () => menu.classList.remove('open')));

  /* ---------- FAQ accordion (animated, one open at a time per group) ---------- */
  const accGroups = new Map();   // container -> [controller]
  document.querySelectorAll('details.qa').forEach(det => {
    const sum = det.querySelector('summary');
    const body = det.querySelector(':scope > *:not(summary)');
    if (!sum || !body) return;
    let anim = null, guard = 0, state = det.open ? 'open' : 'closed';
    const group = det.parentElement;
    if (!accGroups.has(group)) accGroups.set(group, []);
    const siblings = accGroups.get(group);
    const chrome = () => {
      const cs = getComputedStyle(det);
      return parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) +
             parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    };
    const settle = (open) => {
      det.open = open;
      det.classList.toggle('is-open', open);
      det.style.height = det.style.overflow = '';
      anim = null; state = open ? 'open' : 'closed';
    };
    const run = (from, to, open) => {
      if (anim) { anim.onfinish = null; anim.cancel(); }
      clearTimeout(guard);
      det.style.overflow = 'hidden';
      det.style.height = from + 'px';
      const a = det.animate({ height: [from + 'px', to + 'px'] },
        { duration: 340, easing: 'cubic-bezier(.33,1,.68,1)' });
      anim = a;
      a.onfinish = () => { clearTimeout(guard); if (anim === a) settle(open); };
      guard = setTimeout(() => { if (anim === a) { a.onfinish = null; a.cancel(); settle(open); } }, 600);
    };
    const openIt = () => {
      if (state === 'open' || state === 'opening') return;
      if (reduce || document.hidden) { det.open = true; det.classList.add('is-open'); state = 'open'; return; }
      state = 'opening';
      const from = det.offsetHeight;
      det.open = true;                         // render body so we can measure
      det.classList.add('is-open');
      requestAnimationFrame(() => { if (state === 'opening') run(from, sum.offsetHeight + body.offsetHeight + chrome(), true); });
    };
    const closeIt = () => {
      if (state === 'closed' || state === 'closing') return;
      if (reduce || document.hidden) { det.open = false; det.classList.remove('is-open'); state = 'closed'; return; }
      state = 'closing';
      det.classList.remove('is-open');         // chevron rotates back immediately
      run(det.offsetHeight, sum.offsetHeight + chrome(), false); // det.open stays true until anim ends
    };
    const ctl = { det, openIt, closeIt, isOpen: () => state === 'open' || state === 'opening' };
    siblings.push(ctl);
    sum.addEventListener('click', (e) => {
      e.preventDefault();
      if (ctl.isOpen()) { closeIt(); return; }
      siblings.forEach(s => { if (s !== ctl) s.closeIt(); });   // one open at a time
      openIt();
    });
    det.classList.toggle('is-open', det.open);
  });
})();

/* ============================================================
   PRODUCT PAGE (uploader w/ thumbnails, pills, gallery, qty)
   ============================================================ */
(() => {
  /* option pills: single-select within each group */
  document.querySelectorAll('.pills').forEach(group => {
    group.querySelectorAll('.pill').forEach(pill => {
      pill.addEventListener('click', () => {
        group.querySelectorAll('.pill').forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');
      });
    });
  });

  /* gallery thumbnail switch */
  const main = document.getElementById('pMain');
  document.querySelectorAll('.thumb').forEach(t => {
    t.addEventListener('click', () => {
      document.querySelectorAll('.thumb').forEach(x => x.classList.remove('is-active'));
      t.classList.add('is-active');
      // srcset beats src, so it has to be replaced too or the first photo stays
      if (main && t.dataset.full) {
        if (t.dataset.srcset) main.srcset = t.dataset.srcset;
        else main.removeAttribute('srcset');
        main.src = t.dataset.full;
        if (t.dataset.alt) main.alt = t.dataset.alt;
      }
    });
  });

  /* quantity stepper */
  const qty = document.getElementById('qty');
  document.querySelectorAll('.qty button').forEach(b => {
    b.addEventListener('click', () => {
      let v = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.step, 10));
      qty.value = v;
    });
  });

  /* photo uploader with live thumbnails */
  const input = document.getElementById('upload');
  const zone = document.getElementById('dropzone');
  const previews = document.getElementById('previews');
  if (!input || !previews) return;
  let files = [];

  const render = () => {
    previews.innerHTML = '';
    files.forEach((f, i) => {
      const url = URL.createObjectURL(f);
      const el = document.createElement('div');
      el.className = 'preview';
      el.innerHTML = `<img src="${url}" alt="${f.name.replace(/"/g,'')}"><button class="preview__x" type="button" aria-label="Remove">×</button>`;
      el.querySelector('.preview__x').addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        files.splice(i, 1); render();
      });
      previews.appendChild(el);
    });
  };

  const add = (list) => {
    [...list].filter(f => f.type.startsWith('image/')).forEach(f => files.push(f));
    render();
  };

  input.addEventListener('change', () => add(input.files));
  if (zone) {
    ['dragenter', 'dragover'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.add('is-drag'); }));
    ['dragleave', 'drop'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.remove('is-drag'); }));
    zone.addEventListener('drop', e => { if (e.dataTransfer?.files) add(e.dataTransfer.files); });
  }
})();

/* ============================================================
   CONVERSION UPLOADER: multi-photo, per-photo qty, live tally
   (Prototype front-end. On Shopify: addAll loops /cart/add.js,
    one line per photo, file + qty + _thumb as line-item props.)
   ============================================================ */
(() => {
  const box = document.getElementById('uploadbox');
  if (!box) return;
  const multi  = box.dataset.multi === 'true';
  const pieces = parseInt(box.dataset.pieces || '1', 10);   // physical pieces per photo (2-pack etc.)
  const unit   = parseFloat(box.dataset.price || '0');       // price per photo/unit
  const max    = parseInt(box.dataset.max || '50', 10);
  const uLabel = box.dataset.unitlabel || 'cutout';
  let products = null;                                       // optional per-row catalog:
  try { products = JSON.parse(box.dataset.products || 'null'); } catch (e) {}  // [{label,price,pieces}]
  const input  = document.getElementById('upload');
  const zone   = document.getElementById('dropzone');
  const list   = document.getElementById('uplist');
  const bbCount = document.getElementById('bbCount');
  const bbPieces = document.getElementById('bbPieces');
  const bbTotal = document.getElementById('bbTotal');
  const addAll  = document.getElementById('addAll');
  const addMore = document.getElementById('addMore');   // compact "add another item"
  const zoneTitle = zone ? zone.querySelector('.dropzone__t b') : null;
  const isFundraiser = !!box.dataset.fundraiser;        // org order module, keeps old behaviour
  const toast = document.getElementById('toast'), toastName = document.getElementById('toastName');
  let rows = [];
  const money = n => '$' + n.toFixed(2);

  function recompute() {
    const units = rows.reduce((s, r) => s + r.qty, 0);
    const total = rows.reduce((s, r) => s + r.qty * (products ? products[r.pi].price : unit), 0);

    // Rows are ORDER LINES, so each one shows its own number and its own money.
    // Testing showed people read the list as "a pile of photos" and went looking
    // for an add-a-product button; numbering and pricing each line makes it read
    // as an order they are building.
    rows.forEach((r, i) => {
      if (r.idxEl) r.idxEl.textContent = 'Item ' + (i + 1);
      if (r.amtEl) r.amtEl.textContent = money(r.qty * (products ? products[r.pi].price : unit));
    });

    if (!rows.length) {
      bbCount.textContent = 'Nothing added yet'; bbPieces.textContent = '';
      bbTotal.textContent = money(0); addAll.disabled = true;
    } else {
      bbCount.textContent = rows.length + (rows.length === 1 ? ' item' : ' items');
      if (products) {
        const pcs = rows.reduce((s, r) => s + r.qty * (products[r.pi].pieces || 1), 0);
        bbPieces.textContent = pcs + (pcs === 1 ? ' piece' : ' pieces');
      } else {
        bbPieces.textContent = pieces > 1
          ? (units + ' × ' + pieces + '-pack = ' + (units * pieces) + ' pieces')
          : (units + ' ' + uLabel + (units === 1 ? '' : 's'));
      }
      bbTotal.textContent = money(total); addAll.disabled = false;
    }
    // The drop target STAYS VISIBLE, above the list, for as long as more photos
    // can be added. It used to disappear the moment the first item landed and
    // hand over to a compact "Add another item" button, which meant drag and
    // drop worked for the first photo only: after that the only way to add was
    // to click through a file dialog. Keeping one persistent drop target is
    // still ONE control, which was the point of the original change.
    // Fundraiser order pages keep the old behaviour on purpose (their rows
    // carry a per-row product select, so the list is the thing to look at).
    const full = (!multi && rows.length >= 1) || rows.length >= max;
    if (isFundraiser) {
      zone.style.display = full || (rows.length > 0 && addMore) ? 'none' : 'flex';
      if (addMore) addMore.hidden = !(multi && rows.length > 0 && !full);
    } else {
      zone.style.display = full ? 'none' : 'flex';
      zone.classList.toggle('dropzone--compact', rows.length > 0);
      if (zoneTitle) {
        zoneTitle.textContent = rows.length === 0
          ? (multi ? 'Add your first item' : 'Add your photo')
          : 'Add another photo';
      }
      if (addMore) addMore.hidden = true;   // the drop target is the one control
    }
  }

  function addRow(file) {
    if ((!multi && rows.length >= 1) || rows.length >= max) return;
    const row = { file, qty: 1, pi: 0 };
    rows.push(row);
    const el = document.createElement('div'); el.className = 'uprow';
    // same "label · N pc(s)" shape the cart lines use
    const packTag = products ? ''
      : ` &middot; <b>${pieces > 1 ? pieces + '-Pack &middot; ' + pieces + ' pcs' : '1 pc'}</b>`;
    const sizeSel = products
      ? `<select class="uprow__size" aria-label="Size">` +
        products.map((p, i) => `<option value="${i}">${p.label} &middot; $${p.price.toFixed(2)}</option>`).join('') +
        `</select>`
      : '';
    el.innerHTML =
      `<img class="uprow__thumb" alt="">
       <div class="uprow__meta"><span class="uprow__idx"></span><div class="uprow__name"></div><div class="uprow__sub">${(file.size/1048576).toFixed(1)}MB${packTag}</div>${sizeSel}</div>
       <div class="uprow__qty"><button type="button" data-s="-1" aria-label="Fewer">−</button><input value="1" readonly aria-label="Copies"><button type="button" data-s="1" aria-label="More">+</button></div>
       <span class="uprow__amt"></span>
       <button class="uprow__x" type="button" aria-label="Remove item">×</button>`;
    el.querySelector('.uprow__name').textContent = file.name;
    row.idxEl = el.querySelector('.uprow__idx');
    row.amtEl = el.querySelector('.uprow__amt');
    el.querySelector('.uprow__size')?.addEventListener('change', (e) => { row.pi = +e.target.value; recompute(); });
    const thumb = el.querySelector('.uprow__thumb');
    if (file.type.startsWith('image/')) { thumb.src = URL.createObjectURL(file); }
    else { thumb.removeAttribute('src'); thumb.textContent = 'PDF'; }
    const qi = el.querySelector('.uprow__qty input');
    el.querySelectorAll('.uprow__qty button').forEach(b => b.addEventListener('click', () => {
      row.qty = Math.max(1, row.qty + parseInt(b.dataset.s, 10)); qi.value = row.qty; recompute();
    }));
    el.querySelector('.uprow__x').addEventListener('click', () => { rows = rows.filter(r => r !== row); el.remove(); recompute(); });
    list.appendChild(el); recompute();
  }

  const accept = f => f.type.startsWith('image/') || f.type === 'application/pdf' || /\.heic$/i.test(f.name);
  const addFiles = files => [...files].forEach(f => { if (accept(f)) addRow(f); });
  input.addEventListener('change', () => { addFiles(input.files); input.value = ''; });
  if (addMore) addMore.addEventListener('click', () => input.click());
  ['dragenter','dragover'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.add('is-drag'); }));
  ['dragleave','drop'].forEach(ev => zone.addEventListener(ev, e => { e.preventDefault(); zone.classList.remove('is-drag'); }));
  zone.addEventListener('drop', e => { if (e.dataTransfer?.files) addFiles(e.dataTransfer.files); });

  // Rows are handed to the add-to-cart loop already resolved, so the loop never
  // has to know whether this page is a single product (product page) or a
  // per-row catalogue (fundraiser org page).
  window.ABU_UPLOADER = {
    rows: () => rows.map(r => {
      const p = products ? products[r.pi] : null;
      return {
        ...r,
        pieces: p ? (p.pieces || 1) : pieces,
        variantId: p ? p.id : null,
        label: p ? p.label : null
      };
    }),
    recompute,
    hasProducts: () => !!products
  };

  addAll.addEventListener('click', () => {
    if (window.ABU_UPLOADER && document.getElementById('abuForm')) return;  // theme handles it
    if (!rows.length) return;
    const n = rows.length;
    if (toast) { if (toastName) toastName.textContent = n + (n === 1 ? ' cutout added' : ' cutouts added'); toast.classList.add('show'); clearTimeout(toast._t); toast._t = setTimeout(() => toast.classList.remove('show'), 2400); }
    document.getElementById('cartBtn')?.animate([{transform:'scale(1)'},{transform:'scale(1.22)'},{transform:'scale(1)'}], {duration:300});
    rows = []; list.innerHTML = ''; recompute();
  });

  recompute();
})();

/* ============================================================
   COLLECTION + GALLERY FILTERS & SORT
   ============================================================ */
(() => {
  const chips = [...document.querySelectorAll('.filterchip[data-f]')];
  if (!chips.length) return;
  const grid = document.querySelector('.pgrid--collection, .masonry');
  if (!grid) return;
  const cards = [...grid.children];
  cards.forEach((c, i) => { c.dataset.idx = i; });
  const count = document.querySelector('.toolbar__count');
  const apply = (f) => {
    let n = 0;
    cards.forEach(c => {
      const show = f === '*' || (c.dataset.tags || '').split(' ').includes(f);
      c.style.display = show ? '' : 'none';
      if (show) n++;
    });
    if (count) count.textContent = n + (n === 1 ? ' product' : ' products');
  };
  chips.forEach(ch => ch.addEventListener('click', () => {
    chips.forEach(x => x.classList.remove('is-active'));
    ch.classList.add('is-active');
    apply(ch.dataset.f);
  }));
  const sel = document.querySelector('.toolbar .select');
  sel?.addEventListener('change', () => {
    const v = sel.value.toLowerCase();
    const sorted = [...cards];
    if (v.includes('low to high')) sorted.sort((a, b) => a.dataset.price - b.dataset.price);
    else if (v.includes('high to low')) sorted.sort((a, b) => b.dataset.price - a.dataset.price);
    else sorted.sort((a, b) => a.dataset.idx - b.dataset.idx);
    sorted.forEach(c => grid.appendChild(c));
  });
})();

/* ============================================================
   CART PAGE: live qty / remove / totals / empty state
   ============================================================ */
(() => {
  const pageEl = document.querySelector('.cartpage');
  if (!pageEl) return;
  const money = n => '$' + n.toFixed(2);
  const parseMoney = s => parseFloat(String(s).replace(/[^0-9.]/g, '')) || 0;
  const items = () => [...pageEl.querySelectorAll('.citem')];
  items().forEach(it => {
    const p = it.querySelector('.citem__price');
    if (!p.dataset.unit) p.dataset.unit = parseMoney(p.textContent);   // per-copy price
    if (!it.dataset.pieces) {               // fall back to the pill text if not declared
      const m = (it.querySelector('.pill2')?.textContent || '').match(/(\d+)\s*pcs?\b/);
      it.dataset.pieces = m ? m[1] : '1';
    }
  });

  function recompute() {
    let photos = 0, pieces = 0, total = 0;
    pageEl.querySelectorAll('.cgroup').forEach(g => {
      let gPhotos = 0, gPieces = 0;
      g.querySelectorAll('.citem').forEach(it => {
        const qty = parseInt(it.querySelector('.citem__qty input').value, 10) || 1;
        const unit = parseFloat(it.querySelector('.citem__price').dataset.unit);
        it.querySelector('.citem__price').textContent = money(unit * qty);
        gPhotos += 1; gPieces += qty * (+it.dataset.pieces);
        total += unit * qty;
      });
      photos += gPhotos; pieces += gPieces;
      const meta = g.querySelector('.cgroup__meta');
      if (meta) {
        meta.textContent = gPhotos + (gPhotos === 1 ? ' photo' : ' photos') +
          ' · ' + gPieces + (gPieces === 1 ? ' piece' : ' pieces') +
          (meta.dataset.material ? ' · ' + meta.dataset.material : '');
      }
      if (!gPhotos) g.remove();
    });
    const countTxt = photos + (photos === 1 ? ' photo' : ' photos') + ' · ' + pieces + (pieces === 1 ? ' piece' : ' pieces');
    const head = pageEl.querySelector('.cart__count');
    if (head) head.textContent = countTxt;
    const row = pageEl.querySelector('.csum__row span');
    if (row) row.textContent = countTxt;
    const sub = pageEl.querySelector('.csum__sub b');
    if (sub) sub.textContent = money(total);
    if (!photos) {
      pageEl.querySelector('.cart__grid')?.setAttribute('hidden', '');
      pageEl.querySelector('.cart__head')?.setAttribute('hidden', '');
      pageEl.querySelector('.cart-empty')?.removeAttribute('hidden');
    }
  }

  pageEl.addEventListener('click', (e) => {
    const minus = e.target.closest('.citem__qty button');
    const x = e.target.closest('.citem__x');
    if (minus) {
      const input = minus.parentElement.querySelector('input');
      const step = minus.textContent.trim() === '+' || minus.innerHTML.includes('+') ? 1 : -1;
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      recompute();
    } else if (x) {
      x.closest('.citem').remove();
      recompute();
    }
  });
  recompute();
})();

/* ============================================================
   FORM FEEDBACK (contact + newsletter, prototype demo)
   ============================================================ */
(() => {
  const toast = document.getElementById('toast');
  const toastName = document.getElementById('toastName');
  const ping = (msg) => {
    if (!toast) return;
    if (toastName) toastName.textContent = msg;
    toast.classList.add('show');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => toast.classList.remove('show'), 2600);
  };
  // The contact and newsletter forms are REAL Shopify forms now. They must be
  // left alone: calling reset() on submit wiped the fields before the browser
  // sent them. Shopify posts, redirects back, and the section renders its own
  // success message, so no JS feedback is needed here at all.
  //
  // Kept only for genuinely inert demo forms, which must opt in with
  // data-demo-form. Nothing does today.
  document.querySelectorAll('form[data-demo-form]').forEach((f) => {
    f.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!f.checkValidity()) return;
      ping(f.dataset.demoForm || 'Sent.');
      f.reset();
    });
  });
})();

/* ============================================================
   PRODUCT REVIEWS: star picker, photo previews, sort, submit
   (Prototype front-end. On Shopify the form posts to the native
    contact endpoint and staff publishes it as a review metaobject.
    See shopify/README-reviews.md.)
   ============================================================ */
(() => {
  const form = document.getElementById('rvForm');
  if (!form) return;
  const openBtn = document.getElementById('rvOpen');
  const cancel  = document.getElementById('rvCancel');
  const stars   = document.getElementById('rvStars');
  const starLbl = document.getElementById('rvStarLabel');
  const list    = document.getElementById('rvList');
  const sortSel = document.getElementById('rvSort');
  const drop    = document.getElementById('rvDrop');
  const photo   = document.getElementById('rvPhoto');
  const previews = document.getElementById('rvPreviews');
  const gate    = document.querySelector('.rvsum__gate');
  const orderNo = document.getElementById('rvOrderNo');
  const WORDS = ['', 'Not great', 'Could be better', 'Good', 'Great', 'Love it'];
  let rating = 0, files = [];

  /* ---- invite gate ----------------------------------------------------
     Reviews are invite-only: the post-delivery email links here with a
     token tied to one order line, so only a verified buyer of THIS product
     can open the form. On Shopify the token is validated server-side before
     the form renders (see shopify/README-reviews.md); here we just read it.  */
  const params = new URLSearchParams(location.search);
  const token  = params.get('review_token');
  const invited = !!token;
  if (invited) {
    if (orderNo && params.get('order')) orderNo.textContent = '#' + params.get('order');
    if (gate) gate.hidden = true;
    openBtn.hidden = false;
  }

  /* ---- open / close ---- */
  const show = (on) => {
    form.hidden = !on;
    openBtn.hidden = on || !invited;
    if (gate) gate.hidden = on || invited;
    if (on) { form.scrollIntoView({ behavior: 'smooth', block: 'center' }); stars.querySelector('button').focus(); }
  };
  openBtn?.addEventListener('click', () => show(true));
  cancel?.addEventListener('click', () => show(false));
  if (invited) show(true);

  /* ---- star picker (click + keyboard) ---- */
  const paint = (v) => {
    [...stars.querySelectorAll('button')].forEach((b, i) => {
      b.classList.toggle('on', i < v);
      b.setAttribute('aria-checked', String(+b.dataset.v === v));
    });
    starLbl.textContent = v ? WORDS[v] : 'Tap to rate';
  };
  stars.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-v]');
    if (!b) return;
    rating = +b.dataset.v; paint(rating);
    const low = document.getElementById('rvLow');
    if (low) low.hidden = rating === 0 || rating >= 4;   // 1-3 goes to us privately
  });
  stars.addEventListener('mouseover', (e) => {
    const b = e.target.closest('button[data-v]');
    if (b) paint(+b.dataset.v);
  });
  stars.addEventListener('mouseleave', () => paint(rating));
  stars.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    e.preventDefault();
    rating = Math.min(5, Math.max(1, rating + (e.key === 'ArrowRight' ? 1 : -1)));
    paint(rating);
    stars.querySelector(`button[data-v="${rating}"]`).focus();
  });

  /* ---- photo previews ---- */
  const render = () => {
    previews.innerHTML = '';
    files.forEach((f, i) => {
      const el = document.createElement('div');
      el.className = 'preview';
      el.innerHTML = `<img alt=""><button class="preview__x" type="button" aria-label="Remove photo">&times;</button>`;
      el.querySelector('img').src = URL.createObjectURL(f);
      el.querySelector('.preview__x').addEventListener('click', (e) => {
        e.preventDefault(); files.splice(i, 1); render(); syncConsent();
      });
      previews.appendChild(el);
    });
  };
  const consentWrap = document.getElementById('rvConsentWrap');
  const consent = document.getElementById('rvConsent');
  const syncConsent = () => { if (consentWrap) consentWrap.hidden = files.length === 0; };
  const addFiles = (fl) => { [...fl].filter(f => f.type.startsWith('image/')).forEach(f => files.push(f)); render(); syncConsent(); };
  photo?.addEventListener('change', () => { addFiles(photo.files); photo.value = ''; });
  if (drop) {
    ['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.add('is-drag'); }));
    ['dragleave', 'drop'].forEach(ev => drop.addEventListener(ev, e => { e.preventDefault(); drop.classList.remove('is-drag'); }));
    drop.addEventListener('drop', e => { if (e.dataTransfer?.files) addFiles(e.dataTransfer.files); });
  }

  /* ---- sort ---- */
  const cards = list ? [...list.querySelectorAll('.rvcard')] : [];
  cards.forEach((c, i) => { c.dataset.idx = i; });
  sortSel?.addEventListener('change', () => {
    const v = sortSel.value.toLowerCase();
    const sorted = [...cards];
    if (v.includes('highest')) sorted.sort((a, b) => b.dataset.rating - a.dataset.rating || a.dataset.idx - b.dataset.idx);
    else if (v.includes('lowest')) sorted.sort((a, b) => a.dataset.rating - b.dataset.rating || a.dataset.idx - b.dataset.idx);
    else sorted.sort((a, b) => a.dataset.idx - b.dataset.idx);
    sorted.forEach(c => list.appendChild(c));
  });

  /* ---- submit ---- */
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const body = document.getElementById('rvBody');
    const name = document.getElementById('rvName');
    let bad = null;
    if (!rating) { bad = stars.querySelector('button'); starLbl.textContent = 'Please pick a rating'; }
    [body, name].forEach(f => {
      const ok = f.value.trim();
      f.style.borderColor = ok ? '' : 'var(--red)';
      if (!ok && !bad) bad = f;
    });
    if (!bad && files.length && consent && !consent.checked) {
      bad = consent; consentWrap.style.color = 'var(--red)';
    }
    if (bad) { bad.focus(); return; }
    document.getElementById('rvFormBody').hidden = true;
    document.getElementById('rvThanks').hidden = false;
    form.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();

/* ============================================================
   PRODUCT DESCRIPTION: collapse long store descriptions
   ============================================================ */
(() => {
  const desc = document.querySelector('.product__desc');
  if (!desc) return;
  const LIMIT = 184;                       // ~11.5rem, matches the CSS clamp
  if (desc.scrollHeight <= LIMIT + 40) return;

  desc.classList.add('is-clamped');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'desc-more';
  btn.setAttribute('aria-expanded', 'false');
  btn.innerHTML = 'Read more <svg class="ic"><use href="#i-chev"/></svg>';
  desc.after(btn);

  btn.addEventListener('click', () => {
    const open = desc.classList.toggle('is-clamped') === false;
    btn.setAttribute('aria-expanded', String(open));
    btn.innerHTML = (open ? 'Read less' : 'Read more') + ' <svg class="ic"><use href="#i-chev"/></svg>';
    if (!open) btn.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
})();

/* ============================================================
   SHOPIFY: real Add to Cart.
   One cart line per uploaded photo. Each line carries the file itself
   plus a canvas-generated thumbnail, as line-item properties, so the
   cart and the packing slip can both show the customer's image.
   ============================================================ */
(() => {
  const form = document.getElementById('abuForm');
  const box = document.getElementById('uploadbox');
  if (!form || !box || !window.ABU_UPLOADER) return;

  const addAll = document.getElementById('addAll');
  const variantInput = document.getElementById('abuVariant');

  // variant pills keep the hidden input and the live unit price in sync
  document.querySelectorAll('#variantPills .pill').forEach(p => {
    p.addEventListener('click', () => {
      document.querySelectorAll('#variantPills .pill').forEach(x => x.classList.remove('is-active'));
      p.classList.add('is-active');
      variantInput.value = p.dataset.variant;
      box.dataset.price = p.dataset.price;
      window.ABU_UPLOADER.recompute();
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const rows = window.ABU_UPLOADER.rows();
    if (!rows.length) return;

    const label = addAll.innerHTML;
    addAll.disabled = true;
    addAll.textContent = 'Adding…';

    try {
      // Fundraiser attribution, written before the lines so it is on the cart
      // even if a later add fails. Order-level, because the 25% is paid on the
      // order, not per line.
      // Read from THIS page's markup, never from session state: only an
      // organisation's own page carries data-fundraiser, so an order placed on a
      // standard product page can never be credited to an organisation.
      const fundName = box.dataset.fundraiser;
      if (fundName) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { 'Fundraiser': fundName, 'Fundraiser page': box.dataset.fundraiserUrl || '' }
          })
        });
      }

      // Order-level facts go on the cart, not onto every line: they describe the
      // order, and repeating them per line makes the admin order unreadable.
      const attrs = {};
      document.querySelectorAll('[data-cartattr]').forEach((el) => {
        const v = (el.value || '').trim();
        if (v) attrs[el.dataset.cartattr] = v;
      });
      if (Object.keys(attrs).length) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      }

      // Per-line facts. Declared in the markup with data-lineprop so the product
      // page and the fundraiser order module share one code path.
      const lineProps = [];
      document.querySelectorAll('[data-lineprop]').forEach((el) => {
        const v = (el.value || '').trim();
        if (v) lineProps.push([el.dataset.lineprop, v]);
      });

      // one request per photo = one cart line per photo
      for (const row of rows) {
        const fd = new FormData();
        // row.variantId is set when the page offers a per-row product picker
        // (fundraiser org pages). Product pages fall back to the hidden input.
        fd.append('id', row.variantId || variantInput.value);
        fd.append('quantity', row.qty);
        fd.append('properties[Photo]', row.file, row.file.name);
        if (row.pieces > 1) fd.append('properties[_pieces]', row.pieces);
        lineProps.forEach(([k, v]) => fd.append('properties[' + k + ']', v));

        const res = await fetch('/cart/add.js', { method: 'POST', body: fd });
        if (!res.ok) throw new Error((await res.json()).description || 'Add to cart failed');
      }
      window.location.href = '/cart';
    } catch (err) {
      addAll.disabled = false;
      addAll.innerHTML = label;
      const toast = document.getElementById('toast'), tn = document.getElementById('toastName');
      if (toast && tn) {
        tn.textContent = err.message || 'Could not add to cart';
        toast.classList.add('show');
        setTimeout(() => toast.classList.remove('show'), 4000);
      }
    }
  });
})();

/* ============================================================
   CONTINUE SHOPPING
   The cart's secondary button points back at the last product page the
   customer viewed (stored by sections/abu-product.liquid). Falls back to the
   collection href already in the markup when there is nothing stored.
   ============================================================ */
(() => {
  const links = document.querySelectorAll('[data-continue-shopping]');
  if (!links.length) return;

  let last = null;
  try {
    last = JSON.parse(sessionStorage.getItem('abuLastProduct') || 'null');
  } catch (e) { /* unreadable storage: keep the fallback */ }
  if (!last || !last.url) return;

  links.forEach((a) => {
    a.href = last.url;
    if (last.title) a.title = 'Back to ' + last.title;
  });
})();

/* ============================================================
   FUNDRAISER ATTRIBUTION: cart-side only
   The organisation is written onto the cart by the add-to-cart handler, from
   the org page's own data attributes. Nothing is remembered across pages, so
   there is no product-page banner to reveal any more. All that is left is the
   cart's "not for this fundraiser" escape, which clears the attributes so a
   wrong attribution is visible and self-correcting rather than costing a payout.
   ============================================================ */
(() => {
  const barClear = document.getElementById('fundBarClear');
  if (!barClear) return;
  barClear.addEventListener('click', async () => {
    barClear.disabled = true;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { 'Fundraiser': '', 'Fundraiser page': '' } })
      });
      window.location.reload();
    } catch (e) {
      barClear.disabled = false;
    }
  });
})();

/* ============================================================================
   PRINT FILE DOWNLOADER  (/pages/print-files)
   Shopify's CDN re-encodes uploads to WebP for anything sending a browser
   Accept header, and still names the result .jpg. A plain fetch() sends
   Accept of star-slash-star, so it gets the stored original back. That one
   difference is the whole fix. Type is then confirmed from the file's MAGIC
   BYTES, never from its name, and the extension is corrected to match.

   Several photos are packed into ONE zip. Chrome silently blocks the second
   and later programmatic downloads when there is no user gesture, so an order
   with two photos used to save only one file while this page still reported
   both as done. One archive is one download, so that rule never applies.
   ========================================================================== */
(() => {
  const src = document.getElementById('pfSrc');
  if (!src) return;
  const out = document.getElementById('pfOut');
  const outBox = document.getElementById('pfOutBox');
  const go = document.getElementById('pfGo');

  const SIG = [
    ['ffd8ff',   'image/jpeg', 'jpg'],
    ['89504e47', 'image/png',  'png'],
    ['47494638', 'image/gif',  'gif'],
    ['25504446', 'application/pdf', 'pdf'],
    ['49492a00', 'image/tiff', 'tif'],
    ['4d4d002a', 'image/tiff', 'tif'],
  ];

  // Shopify's admin shows a SEPARATE 75x100 thumbnail named <hash>_small.<ext>,
  // not the customer's file. Copying that preview's address gives a 4KB
  // thumbnail instead of the 2MB original, so strip _small before fetching.
  // Measured on a real order: _small 4,442 bytes vs the real file 2,039,917.
  const deThumb = (u) => u.replace(/_small(\.[A-Za-z0-9]+)(?=$|\?)/, '$1');

  // filename supplied by the caller, keyed by full-size URL
  const NAMES = {};
  // optional ?z= archive name, see archiveName()
  let archiveHint = '';

  const findUrls = (t) => [...new Set(
    (t.match(/https?:\/\/[^\s"'<>\\)\]]+/g) || [])
      .filter(u => /cdn\.shopify\.com/i.test(u))
      .map(u => u.replace(/[.,;]+$/, ''))
  )];

  // Takes the raw bytes, not a Blob: the bytes are needed anyway to CRC them
  // for the zip, so reading the file twice would be wasted work.
  function sniff(bytes) {
    const hex = [...bytes.slice(0, 16)].map(x => x.toString(16).padStart(2, '0')).join('');
    for (const [sig, mime, ext] of SIG) if (hex.startsWith(sig)) return { mime, ext };
    if (hex.slice(0, 8) === '52494646' && hex.slice(16, 24) === '57454250') return { mime: 'image/webp', ext: 'webp' };
    if (hex.slice(8, 16) === '66747970') return { mime: 'image/heic', ext: 'heic' };
    return { mime: 'application/octet-stream', ext: 'bin' };
  }

  function row(name, tag, cls, detail) {
    const d = document.createElement('div');
    d.className = 'pf__row';
    d.innerHTML = `<span class="pf__tag pf__tag--${cls}"></span>`
      + `<span class="pf__name"></span><span class="pf__detail"></span>`;
    d.querySelector('.pf__tag').textContent = tag;
    d.querySelector('.pf__name').textContent = name;
    d.querySelector('.pf__detail').textContent = detail || '';
    return d;
  }

  // Hand one finished Blob to the browser as a download.
  function save(blob, name) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 60000);
  }

  // Two photos on one order must not collapse into one file inside the zip.
  function uniqueName(name, used) {
    const key = name.toLowerCase();
    if (!used.has(key)) { used.add(key); return name; }
    const dot = name.lastIndexOf('.');
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : '';
    let n = 2, candidate;
    do { candidate = stem + ' (' + (n++) + ')' + ext; } while (used.has(candidate.toLowerCase()));
    used.add(candidate.toLowerCase());
    return candidate;
  }

  // The order email names every file "<order> - <customer> - <n> - <product>",
  // so the longest common prefix of those names IS the order and customer,
  // which is what the archive should be called. ?z= overrides it if the email
  // ever starts sending an explicit name.
  function archiveName(names) {
    if (archiveHint) return archiveHint;
    const supplied = names.filter(Boolean);
    if (!supplied.length) return 'print files';
    let prefix = supplied[0];
    for (const n of supplied.slice(1)) {
      let i = 0;
      while (i < prefix.length && i < n.length && prefix[i] === n[i]) i++;
      prefix = prefix.slice(0, i);
    }
    // The prefix stops mid-separator, on " - " or partway through the index,
    // so trim any trailing separator and digits back off.
    prefix = prefix.replace(/[\s\-_]+\d*$/, '').trim();
    return prefix || 'print files';
  }

  // ZIP, store method. Deflating JPEGs saves almost nothing and costs CPU, so
  // the bytes go in uncompressed. Bit 0x800 marks the names as UTF-8, which
  // matters because product titles carry inch marks and accents.
  const CRC_TABLE = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      t[n] = c >>> 0;
    }
    return t;
  })();

  function crc32(bytes) {
    let c = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
  }

  function zip(files) {
    const enc = new TextEncoder();
    const now = new Date();
    const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
    const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
    const local = [], central = [];
    let offset = 0;

    for (const f of files) {
      const nameBytes = enc.encode(f.name);
      const sum = crc32(f.bytes);
      const headerAt = offset;

      const lh = new DataView(new ArrayBuffer(30));
      lh.setUint32(0, 0x04034b50, true);          // local file header
      lh.setUint16(4, 20, true);                  // version needed
      lh.setUint16(6, 0x800, true);               // UTF-8 names
      lh.setUint16(8, 0, true);                   // method 0, stored
      lh.setUint16(10, dosTime, true);
      lh.setUint16(12, dosDate, true);
      lh.setUint32(14, sum, true);
      lh.setUint32(18, f.bytes.length, true);     // compressed size
      lh.setUint32(22, f.bytes.length, true);     // uncompressed size
      lh.setUint16(26, nameBytes.length, true);
      lh.setUint16(28, 0, true);                  // no extra field
      local.push(new Uint8Array(lh.buffer), nameBytes, f.bytes);
      offset += 30 + nameBytes.length + f.bytes.length;

      const ch = new DataView(new ArrayBuffer(46));
      ch.setUint32(0, 0x02014b50, true);          // central directory entry
      ch.setUint16(4, 20, true);                  // version made by
      ch.setUint16(6, 20, true);                  // version needed
      ch.setUint16(8, 0x800, true);
      ch.setUint16(10, 0, true);
      ch.setUint16(12, dosTime, true);
      ch.setUint16(14, dosDate, true);
      ch.setUint32(16, sum, true);
      ch.setUint32(20, f.bytes.length, true);
      ch.setUint32(24, f.bytes.length, true);
      ch.setUint16(28, nameBytes.length, true);
      ch.setUint16(30, 0, true);                  // extra length
      ch.setUint16(32, 0, true);                  // comment length
      ch.setUint16(34, 0, true);                  // disk number
      ch.setUint16(36, 0, true);                  // internal attrs
      ch.setUint32(38, 0, true);                  // external attrs
      ch.setUint32(42, headerAt, true);           // where the local header is
      central.push(new Uint8Array(ch.buffer), nameBytes);
    }

    const centralSize = central.reduce((a, x) => a + x.length, 0);
    const eocd = new DataView(new ArrayBuffer(22));
    eocd.setUint32(0, 0x06054b50, true);          // end of central directory
    eocd.setUint16(4, 0, true);
    eocd.setUint16(6, 0, true);
    eocd.setUint16(8, files.length, true);
    eocd.setUint16(10, files.length, true);
    eocd.setUint32(12, centralSize, true);
    eocd.setUint32(16, offset, true);
    eocd.setUint16(20, 0, true);                  // no archive comment
    return new Blob([...local, ...central, new Uint8Array(eocd.buffer)], { type: 'application/zip' });
  }

  async function run(urls) {
    outBox.hidden = false;
    out.textContent = '';
    if (!urls.length) { out.appendChild(row('No links found', 'NONE', 'bad', 'Paste a cdn.shopify.com link')); return; }
    go.disabled = true;

    // Fetch everything FIRST, then save once. Nothing is written to disk until
    // every photo is in hand, so a part-saved order is not possible.
    const got = [];
    const used = new Set();

    for (const u of urls) {
      // Name the saved file after the FULL size original, never the _small
      // preview, or production ends up with files called _small that are not.
      const base = decodeURIComponent(deThumb(u).split('/').pop().split('?')[0]);
      const pending = row(base, '...', 'wait', 'fetching');
      out.appendChild(pending);
      try {
        // Try the full-size file first. If the de-thumbnailed name does not
        // exist, fall back to exactly what was pasted rather than failing.
        const full = deThumb(u);
        let res = await fetch(full, { cache: 'no-store' });
        if (!res.ok && full !== u) res = await fetch(u, { cache: 'no-store' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const bytes = new Uint8Array(await res.arrayBuffer());
        const { mime, ext } = sniff(bytes);
        // Keep the customer's own extension when it already matches the real
        // type (.jpeg stays .jpeg), and only rewrite it when it lied.
        const had = (base.match(/\.([a-z0-9]+)$/i) || [, ''])[1].toLowerCase();
        const sameFamily = (had === ext) || (ext === 'jpg' && (had === 'jpeg' || had === 'jpe'));
        let name = sameFamily ? base : base.replace(/\.[a-z0-9]+$/i, '') + '.' + ext;
        // A supplied name always wins: it carries the order, customer, product
        // and quantity, which is what production actually files by.
        const given = NAMES[deThumb(u)];
        let cleanGiven = '';
        if (given) {
          // Shopify stores product titles with HTML entities in them, so a
          // 12"x18" Big Head arrives as 12&quot;x18&quot;. Decode first, or the
          // entity ends up in the saved filename.
          const ta = document.createElement('textarea');
          ta.innerHTML = given;
          cleanGiven = ta.value.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
          if (cleanGiven) name = cleanGiven + '.' + (sameFamily && had ? had : ext);
        }
        name = uniqueName(name, used);
        got.push({ name, bytes, mime, given: cleanGiven });
        const ok = mime !== 'image/webp';
        pending.replaceWith(row(name, ok ? 'ORIGINAL' : 'WEBP', ok ? 'ok' : 'bad',
          `${mime.replace('image/', '').toUpperCase()} · ${(bytes.length / 1024).toFixed(0)} KB`));
      } catch (e) {
        pending.replaceWith(row(base, 'FAILED', 'bad', String(e.message || e)));
      }
    }

    // One photo saves as itself. Two or more go into a single zip, because
    // Chrome blocks a page from saving several files without a user gesture
    // and silently drops everything after the first.
    if (got.length === 1) {
      save(new Blob([got[0].bytes], { type: got[0].mime }), got[0].name);
    } else if (got.length > 1) {
      const zipName = archiveName(got.map(f => f.given)) + '.zip';
      const blob = zip(got);
      save(blob, zipName);
      out.appendChild(row(zipName, 'ZIP', 'ok',
        `${got.length} photos · ${(blob.size / 1048576).toFixed(1)} MB · one download`));
    }
    go.disabled = false;
  }

  go.addEventListener('click', () => run(findUrls(src.value)));
  document.getElementById('pfClear').addEventListener('click', () => {
    src.value = ''; out.textContent = ''; outBox.hidden = true;
  });

  // A link can carry a whole order: ?f=<url>&f=<url>, and optionally a
  // matching ?n=<filename> for each one. The order email builds those names
  // from Liquid (order number, customer, product, quantity), because this page
  // is handed URLs only and cannot know any of that by itself.
  //
  // MEASURED 18 Aug on order #2754: Shopify's email click tracker (/_t/c/v3/)
  // rewrites the button URL and COLLAPSES repeated query keys, so of N f=
  // pairs only the last one survived the redirect. The email therefore packs
  // everything into ONE fs= and ONE ns=: components double url_encoded, joined
  // with commas. Double, because the tracker decodes and re-encodes values
  // once, and a name can itself contain a comma. After one decode by
  // URLSearchParams the separators are bare commas, content commas are still
  // %2C, and spaces are + signs. Plain f=/n= still works for hand-built links.
  const qp = new URLSearchParams(location.search);
  let qs = qp.getAll('f').filter(Boolean);
  let qn = qp.getAll('n');
  const unpack = (s) => (s || '').split(',').filter(Boolean).map(x => {
    try { return decodeURIComponent(x.replace(/\+/g, ' ')); } catch (e) { return x; }
  });
  const packedF = qp.get('fs');
  if (packedF) { qs = unpack(packedF); qn = unpack(qp.get('ns')); }
  archiveHint = (qp.get('z') || '').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
  if (qs.length) {
    qs.forEach((u, i) => { if (qn[i]) NAMES[deThumb(u)] = qn[i]; });
    src.value = qs.join('\n');
    run(findUrls(src.value));
  }
})();
